import * as React from "react";
import ReactDOM from "react-dom";
import { AllIconNames, ComboBox as BaseComboBox, Hint, MenuHeader, MenuSeparator } from "react-ui";
import { DigestQueryFilter } from "Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";

import { InternalIcon } from "../../Icons/Icon";
import { PicklistDataItem, PicklistQueryResponse } from "../../../Engine/Core/Picklists/PicklistTypes";
import { getTotalCountText } from "../../../Helpers/textForTotalCount";
import { getHintWidth } from "../../constants";
import { MetricsValueSenderActions } from "../../Internals/Metrics/MetricsValueSender";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { DataPasteContext } from "../../MultiControls/MultiLine/TableDataPasteContext";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { pullContext } from "../../TypingUtils";
import { ExcelPasteUtils } from "../../MultiControls/MultiLine/ExcelPasteUtils";
import { IScrollToContext, scrollToContextTypes } from "../../Internals/Contexts/ScrollToContext";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ValueEditorsFocusManager } from "../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { isDOMRectInView } from "../../../Helpers/BrowserHelpers";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";
import { Text } from "../../ValueViewers/Text/Text";
import { TextOverflowHint } from "../../Internals/TextOverflowHint/TextOverflowHint";
import { schedulerWrapper } from "../../../Helpers/Scheduler/SchedulerApi";

import { HighlightWrapper, ReadonlyValueWrapper, TotalCount, ValueName, ValueWrapper } from "./ComboBox.styles";
import { ComboboxItem } from "./ComboboxItem";
import { ComboBoxDataItem, getComboBoxItems, getPicklistItem, isComboBoxDataItem } from "./ComboboxItemProvider";
import { BuildFilterCallback } from "./ComboboxFilterBuilder";
import { getColumnNames } from "./getColumnNames";
import { ComboboxWithAuto } from "./ComboboxWithAuto";

export interface ComboBoxProps extends ControlTooltipProps, ControlValidationProps {
    bindingPath: AbsoluteModelPath;
    value?: string | number | null;
    onChange: (value: string | number, item?: PicklistDataItem) => void;
    filterCallback?: (items: Array<ComboBoxDataItem | JSX.Element>) => Promise<Array<ComboBoxDataItem | JSX.Element>>;
    description?: string;
    disabled?: boolean;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    display?: string;
    displayItem?: "value" | "name";
    id?: string;
    isTooltipEnabled?: boolean;
    gId?: string;
    gPaths?: string[];
    limit?: number;
    enumerations?: PicklistDataItem[];
    menuAlign?: "left" | "right";
    openButton?: boolean;
    placeholder?: string;
    saveDescription?: boolean;
    setDescription: (value: string | undefined) => void;
    setItemId: (id: string | undefined) => void;
    useIncorrectValue?: boolean;
    metrics: MetricsValueSenderActions;
    width?: string | number;
    filter?: BuildFilterCallback;
    notFoundText?: string;
    mostLikelyCaption?: string;
    mostLikelyFilter?: BuildFilterCallback;
    emptyValueFilter?: BuildFilterCallback;
    setPicklistDataItem?: (value: undefined | PicklistDataItem) => void;
    onQueryPicklist: (filter: DigestQueryFilter) => Promise<PicklistQueryResponse>;
    highlighted: boolean;
    keyColumnName?: string;
    valueColumnName?: string;
    searchColumnNames?: string[];
    valueToSaveColumnName?: string;
    isLink?: boolean;
    isReadOnly?: boolean;
    onClick?: () => void;
    autoFlag?: boolean;
    autoValue?: string;
    formula?: React.ReactNode;
    auto?: boolean;
    greyPicklistValues?: boolean;
    useAutoIcon?: AllIconNames;
    "data-tid"?: string;
    isEnableCaseIndependentValidation?: boolean;
    onBlur: () => void;
}

export interface ComboBoxState {
    description?: string;
    item?: ComboBoxDataItem;
    suitableCount: number;
    totalCountText: string;
    isHintRequired: boolean;
}

export class ComboBox extends React.Component<ComboBoxProps, ComboBoxState> {
    private readonly combobox = React.createRef<BaseComboBox<ComboBoxDataItem>>();
    private readonly renderedValue = React.createRef<HTMLDivElement>();
    private lastSource: readonly ComboBoxDataItem[] = [];
    private totalItemCount = 0;
    private isFirstRequest = true;
    private searchTextLength = 0;
    private currentValue?: string | number | null = this.props.value;

    private readonly columnNames = getColumnNames(this.props);
    private readonly keyColumnName = this.columnNames.keyColumnName;
    private readonly valueColumnName = this.columnNames.valueColumnName;

    public state: ComboBoxState = {
        suitableCount: 0,
        totalCountText: "",
        isHintRequired: false,
    };

    public static contextTypes = scrollToContextTypes;

    public async componentDidMount(): Promise<void> {
        if (this.props.value != undefined && this.props.value !== "") {
            await this.updateValueFromProps(this.props.value, true);
        }
    }

    public handleKeyPress = (event: React.KeyboardEvent): void => {
        if (event.key === "Enter") {
            // ждем пока свернется пиклист и уйдут лишние спаны, чтобы по новой собрать все контроллы для фокуса
            this.delayAfterComboboxWillClose();
        }
        this.props.focusByEnterOnNextControl(event);
    };

    public async UNSAFE_componentWillReceiveProps(nextProps: Readonly<ComboBoxProps>): Promise<void> {
        if (nextProps.value !== this.currentValue) {
            this.currentValue = nextProps.value;
            if (nextProps.value == undefined || nextProps.value === "") {
                this.setState({ item: undefined });
                this.props.setPicklistDataItem?.(undefined);
            } else if (nextProps.value) {
                await this.updateValueFromProps(nextProps.value);
            }
        }
    }

    public componentDidUpdate(prevProps: Readonly<ComboBoxProps>): void {
        if (prevProps.value !== this.props.value) {
            this.checkHintRequired();
        }
    }

    public render(): JSX.Element {
        const { description, item } = this.state;
        if (this.props.isReadOnly) {
            let value;
            switch (this.props.display) {
                case "name":
                    value = this.state.item?.picklistDataItem.name?.toString() ?? "";
                    break;
                case "value":
                    value = this.state.item?.picklistDataItem.code?.toString() ?? "";
                    break;
                default:
                    value = `${this.state.item?.picklistDataItem.code?.toString() ?? ""} ${
                        this.state.item?.picklistDataItem.name?.toString() ?? ""
                    }`;
                    break;
            }

            return (
                <TextOverflowHint text={value} width={20}>
                    <ReadonlyValueWrapper>
                        <Text
                            data-tid={this.props["data-tid"]}
                            bindingPath={this.props.bindingPath}
                            value={value}
                            isLink={this.props.isLink}
                            onClick={this.props.onClick}
                        />
                    </ReadonlyValueWrapper>
                </TextOverflowHint>
            );
        }
        const { error, isTooltipEnabled, warning } = this.props;

        const tooltipMessage = error || warning;
        const tooltipTrigger = tooltipMessage && isTooltipEnabled ? "hover&focus" : "closed";
        const tooltipPosition = this.props.tooltipPosition ?? "top left";

        const hintOpened = tooltipTrigger === "closed" && Boolean(description) && this.state.isHintRequired;
        const hintMaxWidth = getHintWidth(description?.length ?? 0);
        const leftIcon = this.props.auto && this.props.autoFlag && (
            <InternalIcon name={this.props.useAutoIcon ?? "MathFunctionIcon"} />
        );

        return (
            <TestPageObjectHandle
                id={this.props.bindingPath}
                pageObject={{
                    getType: () => "ComboBox",
                    isFocused: () =>
                        document.activeElement ===
                        toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.querySelector("input"),
                    isInView: () =>
                        isDOMRectInView(toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.getBoundingClientRect()),
                    getValue: () => this.props.value,
                    setValue: (value: string | number) => this.props.onChange(value),
                    getAutoFlag: () => this.props.autoFlag,
                }}>
                <DataPasteContext.Consumer>
                    {handlePaste => (
                        // onPaste={handlePaste} находится здесь, а не в BaseComboBox потомучто на момент написания кода в rect-ui не пробрасываются onPaste
                        <FocusManagementObserver onRequestFocus={this.handleRequestFocus}>
                            <HighlightWrapper
                                wrapperWidth={this.props.width}
                                highlighted={this.props.highlighted}
                                onPaste={ExcelPasteUtils.WrapHandlePaste(handlePaste, this.props.bindingPath, () =>
                                    this.combobox.current?.reset()
                                )}>
                                <Tooltip
                                    pos={tooltipPosition}
                                    trigger={tooltipTrigger}
                                    value={tooltipMessage}
                                    width={this.props.tooltipWidth}>
                                    <Hint maxWidth={hintMaxWidth} text={hintOpened ? description : undefined}>
                                        <ComboboxWithAuto comboboxProps={this.props}>
                                            <BaseComboBox
                                                data-tid={this.props["data-tid"]}
                                                disabled={this.props.disabled}
                                                error={Boolean(error)}
                                                getItems={this.getItems}
                                                onValueChange={this.handleChange}
                                                onFocus={this.handleFocus}
                                                onBlur={this.handleBlur}
                                                onUnexpectedInput={this.handleUnexpectedInput}
                                                placeholder={this.props.placeholder}
                                                ref={this.combobox}
                                                renderItem={this.renderItem}
                                                menuAlign={this.props.menuAlign}
                                                renderTotalCount={this.renderTotalCount}
                                                renderNotFound={
                                                    this.props.notFoundText ? this.renderNotFound : undefined
                                                }
                                                renderValue={this.renderValue}
                                                itemToValue={this.itemToValue}
                                                totalCount={this.state.suitableCount}
                                                value={item}
                                                valueToString={this.valueToString}
                                                warning={Boolean(warning)}
                                                width={"100%"}
                                                onInputKeyDown={this.handleKeyPress}
                                                leftIcon={leftIcon}
                                            />
                                        </ComboboxWithAuto>
                                    </Hint>
                                </Tooltip>
                            </HighlightWrapper>
                        </FocusManagementObserver>
                    )}
                </DataPasteContext.Consumer>
            </TestPageObjectHandle>
        );
    }

    private async updateValueFromProps(inputValue: string | number, init?: boolean): Promise<void> {
        const item = this.props.enumerations
            ? this.tryGetPicklistItemsFromEnumerations(inputValue)
            : await getPicklistItem(inputValue, this.props);
        if (inputValue === this.currentValue) {
            let picklistItem: PicklistDataItem = {
                ...item,
                [this.valueColumnName]: item?.[this.valueColumnName] ?? "",
                [this.keyColumnName]: this.props.useIncorrectValue ? inputValue : (item?.[this.keyColumnName] ?? ""),
            };
            if (this.props.isEnableCaseIndependentValidation) {
                const itemLowerCase = this.props.enumerations
                    ? this.tryGetPicklistItemsFromEnumerations(inputValue)
                    : await getPicklistItem(inputValue.toString().toLowerCase(), this.props);
                const itemUpperCase = this.props.enumerations
                    ? this.tryGetPicklistItemsFromEnumerations(inputValue)
                    : await getPicklistItem(inputValue.toString().toUpperCase(), this.props);
                picklistItem = {
                    [this.valueColumnName]:
                        item?.[this.valueColumnName] ??
                        itemLowerCase?.[this.valueColumnName] ??
                        itemUpperCase?.[this.valueColumnName] ??
                        "",
                    [this.keyColumnName]: this.props.useIncorrectValue
                        ? inputValue
                        : (item?.[this.keyColumnName] ?? ""),
                };
            }
            if (item != undefined && item.id != undefined) {
                picklistItem.id = item.id;
            }
            if (init && picklistItem[this.keyColumnName] === "") {
                picklistItem[this.keyColumnName] = inputValue;
            }
            this.setPicklistItem(picklistItem);
            this.props.setPicklistDataItem?.(item);
        }
    }

    private tryGetPicklistItemsFromEnumerations(inputValue: string | number): PicklistDataItem | undefined {
        return this.props.enumerations?.find(enumeration =>
            Object.values(enumeration).some(value => value === inputValue.toString())
        );
    }

    private readonly handleRequestFocus = (): void => {
        if (this.combobox.current) {
            const comboboxNode = ReactDOM.findDOMNode(this.combobox.current);
            if (comboboxNode && comboboxNode instanceof Element) {
                this.getContext().scrollTo(comboboxNode);
                this.combobox.current.focus();
            }
        }
    };

    private readonly handleChange = async (item: ComboBoxDataItem): Promise<void> => {
        let currentValue = item.picklistDataItem[this.keyColumnName];
        if (this.props.valueToSaveColumnName) {
            currentValue = item.picklistDataItem[this.props.valueToSaveColumnName];
        }
        if (currentValue == undefined) {
            return;
        }
        this.props.metrics?.sendValue(
            this.props.bindingPath,
            currentValue?.toString(),
            this.searchTextLength,
            this.totalItemCount,
            this.props.limit,
            this.props.gId,
            "ComboBox"
        );

        this.currentValue = currentValue;
        this.props.onChange(currentValue, item.picklistDataItem);

        if (this.props.isEnableCaseIndependentValidation && currentValue) {
            const defaultPicklistItem = await getPicklistItem(currentValue ?? "", this.props);
            const lowerCasePicklistItem = await getPicklistItem(
                currentValue?.toString().toLowerCase() ?? "",
                this.props
            );
            const upperCasePicklistItem = await getPicklistItem(
                currentValue?.toString().toUpperCase() ?? "",
                this.props
            );
            const comboboxItem: ComboBoxDataItem = {
                picklistDataItem: {
                    [this.valueColumnName]:
                        defaultPicklistItem?.[this.valueColumnName] ??
                        lowerCasePicklistItem?.[this.valueColumnName] ??
                        upperCasePicklistItem?.[this.valueColumnName] ??
                        "",
                    [this.keyColumnName]: this.props.useIncorrectValue
                        ? currentValue
                        : // @ts-ignore Херня какая-то выглядит как баг. Дебажить щас нет времени
                          (item?.[this.keyColumnName] ?? ""),
                },
            };
            this.setPicklistItem(comboboxItem.picklistDataItem);
            this.props.setPicklistDataItem?.(comboboxItem.picklistDataItem);
        } else {
            this.setPicklistItem(item.picklistDataItem);
            this.props.setPicklistDataItem?.(item.picklistDataItem);
        }
    };

    private readonly handleUnexpectedInput = (query: string): ComboBoxDataItem => {
        const picklistItem = this.lastSource.find(
            ({ picklistDataItem }) => picklistDataItem[this.keyColumnName]?.toString() === query
        );
        return picklistItem
            ? picklistItem
            : { picklistDataItem: { [this.keyColumnName]: this.props.useIncorrectValue ? query : "", name: "" } };
    };

    private readonly getItems = async (pattern: string): Promise<Array<ComboBoxDataItem | JSX.Element>> => {
        const { itemsInfo, commonItemsInfo } = await getComboBoxItems({
            pattern: pattern,
            comboboxProps: this.props,
        });
        const { items, itemsCount } = itemsInfo;
        const { commonItems, commonItemsSuitableCount, search } = commonItemsInfo;

        if (commonItemsSuitableCount > 0) {
            this.lastSource = commonItems;
            this.searchTextLength = search.pattern.length;

            if (this.isFirstRequest) {
                this.isFirstRequest = false;
                this.totalItemCount = commonItemsSuitableCount;
            }

            this.setState({
                suitableCount: commonItemsSuitableCount,
                totalCountText: getTotalCountText(itemsCount, commonItemsSuitableCount, Boolean(pattern)),
            });
        }

        return items;
    };

    private readonly renderTotalCount = (): React.ReactNode => (
        <TotalCount data-tid="total-count-tip">{this.state.totalCountText}</TotalCount>
    );

    private readonly renderNotFound = (): React.ReactNode => (
        <TotalCount data-tid="total-count-tip">{this.props.notFoundText}</TotalCount>
    );

    private readonly handleFocus = (): void => {
        this.props.metrics?.saveValue(this.props.value?.toString());
    };

    private readonly handleBlur = (): void => {
        this.checkHintRequired();
        this.props.onBlur();
        // Нужно обновить массив контроллов для перехода по enter, тк комбобокс изменяет span в дом дереве и нужно получить новый
        this.delayAfterComboboxWillClose();
    };

    private readonly delayAfterComboboxWillClose = (): void => {
        setTimeout(() => {
            ValueEditorsFocusManager.forceUpdateFocusedByEnterControls();
        }, 0);
    };

    private readonly valueToString = (comboBoxItem: ComboBoxDataItem): string => {
        const item = comboBoxItem.picklistDataItem;
        switch (this.props.display) {
            case "value":
            case "item":
            case undefined:
                return item[this.keyColumnName]?.toString() ?? "";
            case "name":
                return (item[this.valueColumnName] || item[this.keyColumnName])?.toString() ?? "";
            default:
                return item[this.props.display]?.toString() ?? "";
        }
    };

    private readonly itemToValue = (item: ComboBoxDataItem | React.ReactElement): string => {
        if (React.isValidElement(item)) {
            // сюда может попать MenuHeader или MenuSeparator из getItems
            if (item.type === MenuHeader || item.type === MenuSeparator) {
                return "______________";
            } else {
                throw new Error(`В элементы комбобокса попал React.ReactElement ${item.type}`);
            }
        }
        if (isComboBoxDataItem(item)) {
            return (
                (item.picklistDataItem.id
                    ? item.picklistDataItem.id
                    : item.picklistDataItem[this.keyColumnName]
                )?.toString() ?? ""
            );
        }

        return "";
    };

    private readonly renderValue = (comboBoxItem: ComboBoxDataItem): React.ReactNode | undefined => {
        const pickListItem = comboBoxItem.picklistDataItem;
        if (pickListItem[this.keyColumnName] === "") {
            return undefined;
        }
        switch (this.props.display) {
            case "value":
                return (
                    <div ref={this.setTextOverflow}>
                        <span>{pickListItem[this.keyColumnName]}</span>
                    </div>
                );
            case "name":
                return (
                    <div ref={this.setTextOverflow}>
                        <span>{pickListItem[this.valueColumnName] || pickListItem[this.keyColumnName]}</span>
                    </div>
                );
            case "item":
            case undefined:
                return (
                    <ValueWrapper ref={this.setTextOverflow}>
                        <span>{pickListItem[this.keyColumnName]}</span>
                        <ValueName>
                            {` `}
                            {pickListItem[this.valueColumnName]}
                        </ValueName>
                    </ValueWrapper>
                );
            default:
                return (
                    <div ref={this.renderedValue}>
                        <span>{pickListItem[this.props.display]}</span>
                    </div>
                );
        }
    };

    private readonly renderItem = (comboBoxItem: ComboBoxDataItem): React.ReactNode => {
        if (!comboBoxItem?.picklistDataItem) {
            return null;
        }

        const pickListItem = comboBoxItem.picklistDataItem;
        const isCodeHighlighted = comboBoxItem.highlightedLabel?.[this.keyColumnName] != undefined;
        const isNameHighlighted = comboBoxItem.highlightedLabel?.[this.valueColumnName] != undefined;
        const highlightedName = comboBoxItem.highlightedLabel?.[this.valueColumnName]?.toString() ?? "";
        const highlightedCode = comboBoxItem.highlightedLabel?.[this.keyColumnName]?.toString() ?? "";
        const dataTid = comboBoxItem.isMostLikelyItem ? "combobox-most-likely-item" : "combobox-item";
        const itemMaxWidth = typeof this.props.width === "number" ? this.props.width : undefined;
        const codeContentProps = {
            content: pickListItem[this.keyColumnName]?.toString() ?? "",
            isHighlighted: isCodeHighlighted,
            highlightedString: highlightedCode,
        };
        const nameContentProps = {
            content: pickListItem[this.valueColumnName]?.toString() ?? "",
            isHighlighted: isNameHighlighted,
            highlightedString: highlightedName,
        };

        return (
            <ComboboxItem
                displayItem={this.props.displayItem}
                dataTid={dataTid}
                itemMaxWidth={itemMaxWidth}
                formula={this.props.formula}
                isAutoValueItem={comboBoxItem.isAutoValueItem}
                codeContentProps={codeContentProps}
                nameContentProps={nameContentProps}
                greyPicklistValues={this.props.greyPicklistValues}
            />
        );
    };

    private setPicklistItem(pickListItem: PicklistDataItem): void {
        schedulerWrapper(() => {
            if (pickListItem.id && this.props.id !== pickListItem.id) {
                this.props.setItemId(pickListItem.id?.toString());
            }
            if (this.props.saveDescription) {
                this.props.setDescription?.(pickListItem[this.valueColumnName]?.toString() ?? "");
            }
            const description = this.props.saveDescription ? undefined : pickListItem[this.valueColumnName]?.toString();

            if (this.props.enumerations) {
                const itemName = this.props.enumerations.find(
                    enumItem =>
                        enumItem[this.keyColumnName]?.toString() === pickListItem[this.keyColumnName]?.toString()
                );

                if (itemName) {
                    const key = itemName[this.keyColumnName];
                    const value = itemName[this.valueColumnName];
                    if (key != undefined && value != undefined) {
                        const pickListEnumItem: PicklistDataItem = {
                            [this.keyColumnName]: key,
                            [this.valueColumnName]: value,
                        };
                        pickListItem = pickListEnumItem;
                    }
                }
            }
            this.setState({ item: { picklistDataItem: pickListItem }, description: description });
        });
    }

    private readonly checkHintRequired = () => {
        this.setTextOverflow(this.renderedValue.current);
    };

    private readonly setTextOverflow = (div: HTMLDivElement | null) => {
        const isHintRequired = !!div && this.hasOverflow(div);
        // @ts-ignore
        this.renderedValue.current = div;
        this.setHintRequired(isHintRequired);
    };

    private readonly hasOverflow = (div: HTMLDivElement): boolean => div.scrollWidth > div.offsetWidth;

    private readonly setHintRequired = (value: boolean) => {
        this.setState({ isHintRequired: value });
    };

    private getContext(): IScrollToContext {
        return pullContext<IScrollToContext>(this);
    }
}
